import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { API } from 'utils/Helper';
import { useQuery } from '@tanstack/react-query';
import storage from 'utils/Storage';
import Cookies from 'js-cookie';

// ─── Shimmer ────────────────────────────────────────────────────────────────
const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

// ─── Wrapper ─────────────────────────────────────────────────────────────────
const Wrapper = styled.section`
  margin: 40px 0 20px 0;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.05);
  background: #292d2e;
  box-shadow: 0 4px 24px rgba(0,0,0,0.25);
`;

// ─── Header bar ──────────────────────────────────────────────────────────────
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  flex-wrap: wrap;
  gap: 10px;
`;

const Title = styled.h2`
  font-size: 15px;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  letter-spacing: 0.3px;
`;

// ─── Pill Tab switcher ────────────────────────────────────────────────────────
const TabRow = styled.div`
  display: inline-flex;
  background: #1e2122;
  border-radius: 9999px;
  padding: 4px;
  gap: 2px;
  border: 1px solid rgba(255,255,255,0.05);
`;

const TabBtn = styled.button`
  padding: 7px 20px;
  border-radius: 9999px;
  border: none;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  background: ${p => p.active ? '#3a4142' : 'transparent'};
  color: ${p => p.active ? '#ffffff' : '#7f898c'};

  &:hover {
    color: #ffffff;
  }
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #3a4142; border-radius: 4px; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
`;

const THead = styled.thead`
  border-bottom: 1px solid rgba(255,255,255,0.05);
`;

const TH = styled.th`
  padding: 11px 16px;
  font-size: 11px;
  font-weight: 700;
  color: #7f898c;
  text-align: ${p => p.right ? 'right' : 'left'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const TR = styled.tr`
  border-bottom: 1px solid rgba(255,255,255,0.04);
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255,255,255,0.03);
  }
`;

const TD = styled.td`
  padding: 11px 16px;
  font-size: 13px;
  font-weight: ${p => p.bold ? '700' : '500'};
  color: ${p => p.muted ? '#7f898c' : '#ffffff'};
  text-align: ${p => p.right ? 'right' : 'left'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${p => p.maxw || 'auto'};
`;

const ProfitText = styled.span`
  color: ${p => p.positive ? '#1fc16b' : '#ff5a7a'};
  font-weight: 700;
`;

// ─── Skeleton rows ────────────────────────────────────────────────────────────
const SkeletonCell = styled.div`
  height: 13px;
  border-radius: 6px;
  width: ${p => p.w || '80%'};
  background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;

const SkeletonRow = () => (
  <TR>
    <TD><SkeletonCell w="55%" /></TD>
    <TD><SkeletonCell w="60%" /></TD>
    <TD><SkeletonCell w="70%" /></TD>
    <TD right><SkeletonCell w="50%" style={{ marginLeft: 'auto' }} /></TD>
    <TD right><SkeletonCell w="45%" style={{ marginLeft: 'auto' }} /></TD>
  </TR>
);

// ─── Empty state ──────────────────────────────────────────────────────────────
const Empty = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #7f898c;
  font-size: 13px;
  font-weight: 600;
`;

// ─── Main component ───────────────────────────────────────────────────────────
function CasinoLeaderboard() {
  const isLogged = !!storage.getKey('logged');
  const [activeTab, setActiveTab] = useState('my');

  // All bets query is kept but not active if activeTab is not 'all'
  const { data: allBets = [], isLoading: allLoading, isError: allError, isFetched: allFetched } = useQuery({
    queryKey: ['liveBets'],
    queryFn: async () => {
      const res = await fetch(`${API}/live-bets`);
      if (!res.ok) throw new Error('Failed to fetch live bets');
      return res.json();
    },
    enabled: activeTab === 'all',
    refetchInterval: 3000,
    refetchOnWindowFocus: false,
    staleTime: 0,
    retry: 1,
  });

  // My bets – use same endpoint as /my-bets page, filtered by logged-in user
  const uid = Cookies.get('uid');
  const { data: myBets = [], isLoading: myLoading, isError: myError, isFetched: myFetched } = useQuery({
    queryKey: ['myBetsHome', uid],
    queryFn: async () => {
      const res = await fetch(`${API}/api/user-bets?uid=${uid}&tab=casino&subTab=settled&limit=10`);
      if (!res.ok) throw new Error('Failed to fetch my bets');
      const json = await res.json();
      // Map getUserBets format → table format
      return (json.data || []).map(row => ({
        game: row.game_name || row.game_id || 'Game',
        user: 'Me',
        betId: row.id?.toString() || '—',
        payout: row.multiplier || '0.00x',
        profit: row.payout != null
          ? (row.payout - row.amount) >= 0
            ? `+${(row.payout - row.amount).toFixed(4)}`
            : (row.payout - row.amount).toFixed(4)
          : '—',
      }));
    },
    enabled: activeTab === 'my' && !!uid,
    refetchInterval: 5000,
    refetchOnWindowFocus: false,
    staleTime: 0,
    retry: 1,
  });

  if (!isLogged) {
    return null;
  }

  const bets     = activeTab === 'my' ? myBets    : allBets;
  const loading  = activeTab === 'my' ? (myLoading && !myFetched) : (allLoading && !allFetched);
  const hasError = activeTab === 'my' ? myError   : allError;

  const tabs = [
    { key: 'my',  label: 'My Bets'  }
  ];

  return (
    <Wrapper>
      {/* ── Header ── */}
      <HeaderBar>
        <Title>Latest Bets</Title>
        <TabRow>
          {tabs.map(t => (
            <TabBtn
              key={t.key}
              active={activeTab === t.key}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </TabBtn>
          ))}
        </TabRow>
      </HeaderBar>

      {/* ── Table ── */}
      <TableWrap>
        <Table>
          <THead>
            <tr>
              <TH>Game</TH>
              <TH>Player</TH>
              <TH>Bet ID</TH>
              <TH right>Payout</TH>
              <TH right>Profit</TH>
            </tr>
          </THead>
          <tbody>
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => <SkeletonRow key={i} />)
            ) : hasError || bets.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <Empty>
                    {hasError
                      ? 'Unable to load bets right now. Please try again later.'
                      : activeTab === 'my'
                      ? 'No bets placed yet. Start playing to see your bets here!'
                      : 'No live bets at the moment.'}
                  </Empty>
                </td>
              </tr>
            ) : (
              bets.map((row, i) => {
                const profit = parseFloat(row.profit);
                return (
                  <TR key={`${row.betId}-${i}`}>
                    <TD bold maxw="160px">{row.game}</TD>
                    <TD muted maxw="140px">{row.user}</TD>
                    <TD muted maxw="130px">{row.betId}</TD>
                    <TD right muted>{row.payout}</TD>
                    <TD right>
                      <ProfitText positive={profit >= 0}>
                        {row.profit}
                      </ProfitText>
                    </TD>
                  </TR>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrap>
    </Wrapper>
  );
}

export default CasinoLeaderboard;